/**
 * Pure model for entity network graphs (NETWORK_GRAPH visuals).
 *
 * Contract: agent-orchestration docs/entity-graph-demographic-relationships-api-ui.md.
 * Entities are linked by money flows (TRANSACTED_WITH) and by shared demographic
 * attributes: attribute nodes (masked values only) reached over HAS_ATTRIBUTE,
 * POSSIBLE_SAME_PARTY between two entities, and SHARED_ATTRIBUTE — the collapsed
 * entity → attribute ← entity form.
 *
 * Kept free of Cytoscape so it can be tested on its own.
 */

export interface GraphNode {
    id: string | number;
    label?: string;
    properties?: Record<string, any>;
}

export interface GraphEdge {
    id?: string | number | null;
    source: string | number;
    target: string | number;
    type?: string;
    properties?: Record<string, any>;
}

export interface Graph {
    nodes: GraphNode[];
    edges: GraphEdge[];
}

/** A graph whose ids are strings, unique, and whose edges all have both endpoints. */
export interface NormalisedNode extends GraphNode { id: string; }
export interface NormalisedEdge extends GraphEdge { id: string; source: string; target: string; type: string; }
export interface NormalisedGraph {
    nodes: NormalisedNode[];
    edges: NormalisedEdge[];
}

export interface StyleMapping {
    nodeTypeColors: Record<string, string>;
    edgeTypeColors: Record<string, string>;
}

export const EDGE = {
    TRANSACTED_WITH: 'TRANSACTED_WITH',
    HAS_ATTRIBUTE: 'HAS_ATTRIBUTE',
    POSSIBLE_SAME_PARTY: 'POSSIBLE_SAME_PARTY',
    SHARED_ATTRIBUTE: 'SHARED_ATTRIBUTE',
} as const;

/** Attribute node labels, with the type icon and the noun used in wording. */
const ATTRIBUTES: Record<string, { type: string; icon: string; noun: string }> = {
    EmailAddress: { type: 'EMAIL', icon: '✉', noun: 'email' },
    PhoneNumber: { type: 'PHONE', icon: '☎', noun: 'phone' },
    Address: { type: 'ADDRESS', icon: '⌂', noun: 'address' },
    TaxId: { type: 'TAX_ID', icon: '#', noun: 'tax id' },
    NationalId: { type: 'NATIONAL_ID', icon: '#', noun: 'national id' },
    LegalEntityId: { type: 'LEI', icon: '#', noun: 'LEI' },
    AccountNumber: { type: 'ACCOUNT_NUMBER', icon: '#', noun: 'account number' },
    IPAddress: { type: 'IP', icon: '⌁', noun: 'IP address' },
    DeviceId: { type: 'DEVICE', icon: '▣', noun: 'device' },
};

const ATTRIBUTE_BY_TYPE: Record<string, { label: string; icon: string; noun: string }> = Object.fromEntries(
    Object.entries(ATTRIBUTES).map(([label, a]) => [a.type, { label, icon: a.icon, noun: a.noun }])
);

/**
 * The producer's STYLE_MAP (agent-orchestration app/utilities/graph_viz_utils.py).
 * Only fills keys a visual's own metadata.styleMapping lacks: older records predate
 * the demographic types.
 */
export const DEFAULT_STYLE_MAPPING: StyleMapping = {
    nodeTypeColors: {
        Entity: '#6366f1',
        PhoneNumber: '#3b82f6',
        EmailAddress: '#10b981',
        IPAddress: '#f59e0b',
        DeviceId: '#ec4899',
        Address: '#8b5cf6',
        TaxId: '#ef4444',
        NationalId: '#dc2626',
        LegalEntityId: '#b91c1c',
        AccountNumber: '#0ea5e9',
    },
    edgeTypeColors: {
        TRANSACTED_WITH: '#94a3b8',
        HAS_ATTRIBUTE: '#cbd5e1',
        POSSIBLE_SAME_PARTY: '#f97316',
        SHARED_ATTRIBUTE: '#a855f7',
    },
};

/**
 * The producer's grey edges are tuned for navy; on white they all but vanish
 * (#cbd5e1 is 1.5:1), so a light surface takes the next slate step down.
 */
const PALE_EDGES_ON_WHITE: Record<string, string> = {
    '#94a3b8': '#64748b',
    '#cbd5e1': '#94a3b8',
};

export const resolveStyleMapping = (
    styleMapping?: Partial<StyleMapping> | null,
    theme: 'dark' | 'light' = 'dark',
): StyleMapping => {
    const edgeTypeColors = { ...DEFAULT_STYLE_MAPPING.edgeTypeColors, ...(styleMapping?.edgeTypeColors || {}) };
    return {
        nodeTypeColors: { ...DEFAULT_STYLE_MAPPING.nodeTypeColors, ...(styleMapping?.nodeTypeColors || {}) },
        edgeTypeColors: theme === 'light'
            ? Object.fromEntries(Object.entries(edgeTypeColors).map(
                ([type, colour]) => [type, PALE_EDGES_ON_WHITE[colour.toLowerCase()] ?? colour]))
            : edgeTypeColors,
    };
};

const hasValue = (v: unknown) => v !== undefined && v !== null && v !== '';

export const isAttributeNode = (node: GraphNode): boolean =>
    (!!node.label && node.label in ATTRIBUTES) || hasValue(node.properties?.display);

/** The noun for an attribute label (`PhoneNumber`) or type (`PHONE`): "phone". */
export const attributeNoun = (labelOrType: string): string => {
    const key = labelOrType.trim();
    return ATTRIBUTES[key]?.noun || ATTRIBUTE_BY_TYPE[key.toUpperCase()]?.noun || key;
};

const attributeIcon = (node: GraphNode): string =>
    (node.label && ATTRIBUTES[node.label]?.icon)
    || ATTRIBUTE_BY_TYPE[String(node.properties?.type || '').toUpperCase()]?.icon
    || '◆';

/** Splits a list that may arrive as an array or as a comma-separated string. */
const toList = (value: unknown): string[] => {
    if (Array.isArray(value)) return value.map(String).map(s => s.trim()).filter(Boolean);
    if (typeof value === 'string') return value.split(',').map(s => s.trim()).filter(Boolean);
    return [];
};

/** Display name: the masked value for attributes, the legal name first for entities. */
export const nodeDisplayName = (node: GraphNode): string => {
    const p = node.properties || {};
    if (isAttributeNode(node)) return `${attributeIcon(node)} ${p.display ?? p.name ?? node.id}`;
    return String(p.legal_name || p.name || p.entity_id || node.id);
};

/**
 * Plain-language description of an edge. Says "shares an email with", never repeats
 * an attribute value: masked values only appear on attribute nodes.
 */
export const edgeDescription = (edge: GraphEdge): string => {
    const p = edge.properties || {};
    // A producer that has already worded the edge (e.g. "owns 60%", "41 payments · 1,206,945") wins.
    if (hasValue(p.description)) return String(p.description);
    switch (edge.type) {
        case EDGE.POSSIBLE_SAME_PARTY: {
            const shared = toList(p.shared_types).map(attributeNoun);
            return `Probably the same party${shared.length ? ` — shared ${shared.join(', ')}` : ''}`;
        }
        case EDGE.SHARED_ATTRIBUTE: {
            const shared = toList(p.attributes).map(attributeNoun);
            return shared.length ? `Shares ${shared.join(', ')}` : 'Shares an attribute';
        }
        case EDGE.HAS_ATTRIBUTE:
            return hasValue(p.source) ? `Has attribute (source: ${p.source})` : 'Has attribute';
        default:
            return edge.type || '';
    }
};

/** The score on a POSSIBLE_SAME_PARTY edge, formatted for its label ("0.93"). */
export const scoreLabel = (edge: GraphEdge): string => {
    const score = Number(edge.properties?.score);
    return edge.type === EDGE.POSSIBLE_SAME_PARTY && Number.isFinite(score) ? score.toFixed(2) : '';
};

/**
 * Coerces raw `{nodes, edges}` into a graph Cytoscape can always build: ids become
 * strings (edge ids may be integers beyond 2^53), duplicates are dropped, and so is
 * any edge whose endpoint isn't a node. `dropped` counts discarded edges.
 */
export const normaliseGraph = (raw: unknown): { graph: NormalisedGraph; dropped: number } => {
    const data = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Partial<Graph>) : {};
    const nodes: NormalisedNode[] = [];
    const nodeIds = new Set<string>();
    for (const n of Array.isArray(data.nodes) ? data.nodes : []) {
        if (!n || !hasValue(n.id)) continue;
        const id = String(n.id);
        if (nodeIds.has(id)) continue;
        nodeIds.add(id);
        nodes.push({ ...n, id });
    }

    const edges: NormalisedEdge[] = [];
    const edgeIds = new Set<string>();
    let dropped = 0;
    for (const e of Array.isArray(data.edges) ? data.edges : []) {
        if (!e) continue;
        const source = String(e.source);
        const target = String(e.target);
        const type = e.type || '';
        if (!nodeIds.has(source) || !nodeIds.has(target)) {
            dropped++;
            continue;
        }
        const id = hasValue(e.id) ? String(e.id) : `${source}-${target}-${type}`;
        if (edgeIds.has(id)) continue;
        edgeIds.add(id);
        edges.push({ ...e, id, source, target, type });
    }
    return { graph: { nodes, edges }, dropped };
};

/** Union of several graphs. Attribute node ids are stable, so a shared one becomes one node. */
export const mergeGraphs = (graphs: unknown[]): NormalisedGraph => {
    const all = graphs.map(g => normaliseGraph(g).graph);
    return normaliseGraph({
        nodes: all.flatMap(g => g.nodes),
        edges: all.flatMap(g => g.edges),
    }).graph;
};

export interface Layers {
    money: boolean;
    attributes: boolean;
    sameParty: boolean;
}

export const ALL_LAYERS: Layers = { money: true, attributes: true, sameParty: true };

const edgeLayer = (type: string): keyof Layers | null => {
    if (type === EDGE.TRANSACTED_WITH) return 'money';
    if (type === EDGE.HAS_ATTRIBUTE || type === EDGE.SHARED_ATTRIBUTE) return 'attributes';
    if (type === EDGE.POSSIBLE_SAME_PARTY) return 'sameParty';
    return null;
};

/** Which relationship layers a graph contains, so toggles only show when they matter. */
export const layersPresent = (graph: NormalisedGraph): Layers & { attributeNodes: boolean } => {
    const present = { money: false, attributes: false, sameParty: false, attributeNodes: false };
    for (const e of graph.edges) {
        const layer = edgeLayer(e.type);
        if (layer) present[layer] = true;
    }
    present.attributeNodes = graph.nodes.some(isAttributeNode);
    return present;
};

/** Hides switched-off layers. Entity nodes always stay; edges of unknown type stay too. */
export const filterLayers = (graph: NormalisedGraph, layers: Layers): NormalisedGraph => {
    const nodes = layers.attributes ? graph.nodes : graph.nodes.filter(n => !isAttributeNode(n));
    const ids = new Set(nodes.map(n => n.id));
    const edges = graph.edges.filter(e => {
        const layer = edgeLayer(e.type);
        return (!layer || layers[layer]) && ids.has(e.source) && ids.has(e.target);
    });
    return { nodes, edges };
};

/**
 * Replaces every entity → attribute ← entity path with one SHARED_ATTRIBUTE edge per
 * entity pair, listing the attribute types. Attribute nodes are removed; an attribute
 * held by a single entity links nothing and so disappears.
 */
export const collapseAttributes = (graph: NormalisedGraph): NormalisedGraph => {
    const attributeNodes = new Map(graph.nodes.filter(isAttributeNode).map(n => [n.id, n]));
    if (attributeNodes.size === 0) return graph;

    // attribute id → entity ids holding it
    const holders = new Map<string, Set<string>>();
    for (const e of graph.edges) {
        if (e.type !== EDGE.HAS_ATTRIBUTE) continue;
        const [attr, entity] = attributeNodes.has(e.target) ? [e.target, e.source] : [e.source, e.target];
        if (!attributeNodes.has(attr) || attributeNodes.has(entity)) continue;
        if (!holders.has(attr)) holders.set(attr, new Set());
        holders.get(attr)!.add(entity);
    }

    const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
    const existingPairs = new Set(
        graph.edges.filter(e => e.type === EDGE.SHARED_ATTRIBUTE).map(e => pairKey(e.source, e.target))
    );

    // entity pair → attribute labels shared
    const shared = new Map<string, Set<string>>();
    for (const [attr, entities] of holders) {
        const label = attributeNodes.get(attr)!.label || 'Attribute';
        const list = [...entities].sort();
        for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
                const key = pairKey(list[i], list[j]);
                if (!shared.has(key)) shared.set(key, new Set());
                shared.get(key)!.add(label);
            }
        }
    }

    const collapsedEdges: NormalisedEdge[] = [];
    for (const [key, labels] of shared) {
        if (existingPairs.has(key)) continue;
        const [source, target] = key.split('|');
        collapsedEdges.push({
            id: `collapsed:${key}`,
            source,
            target,
            type: EDGE.SHARED_ATTRIBUTE,
            properties: { attributes: [...labels].join(', ') },
        });
    }

    return {
        nodes: graph.nodes.filter(n => !attributeNodes.has(n.id)),
        edges: [
            ...graph.edges.filter(e => !attributeNodes.has(e.source) && !attributeNodes.has(e.target)),
            ...collapsedEdges,
        ],
    };
};

/** Cytoscape element data. Structural fields are explicit; the record's own properties live under `props`. */
export const toCyElements = (graph: NormalisedGraph) => [
    ...graph.nodes.map(n => ({
        data: {
            id: n.id,
            label: n.label || 'Unknown',
            kind: isAttributeNode(n) ? 'attribute' : 'entity',
            name: nodeDisplayName(n),
            strength: String(n.properties?.strength || ''),
            props: n.properties || {},
        },
    })),
    ...graph.edges.map(e => ({
        data: {
            id: e.id,
            source: e.source,
            target: e.target,
            type: e.type,
            description: edgeDescription(e),
            score: scoreLabel(e),
            props: e.properties || {},
        },
    })),
];

// ─── Patterns (one per network visual) ───────────────────────────────────────

export interface NetworkItemLike {
    objectType?: string;
    label?: string;
    metadata?: Record<string, any>;
    entries?: { key?: string; label?: string; value: any }[];
    value?: { title?: string; data?: any; metadata?: Record<string, any> };
}

export interface PatternSummary {
    index: number;
    title: string;
    severity: string;
    analysis: string;
    entityIds: string[];
    isSameParty: boolean;
}

export const itemMetadata = (item: NetworkItemLike): Record<string, any> =>
    ({ ...(item.metadata || {}), ...(item.value?.metadata || {}) });

export const isNetworkGraphItem = (item: NetworkItemLike): boolean => {
    const meta = itemMetadata(item);
    const visType = String(meta.visualizationType || meta.visualizationtype || '').toUpperCase();
    const data = item.value?.data;
    return item.objectType === 'MULTICOLUMNDICT' && visType === 'NETWORK_GRAPH'
        && !!data && typeof data === 'object' && !Array.isArray(data);
};

/**
 * Where a group's combined network view goes: in place of its last network visual, or
 * -1 when there are fewer than two to combine. The view therefore follows the network
 * items' own order (their informationSubcategory numbering) instead of heading the
 * group above the report it illustrates. The last, not the first: a report that keeps
 * an early, narrow graph (an ownership tree beside its table) and a later, wider one (a
 * network map) wants the merged view where the wider one is read.
 */
export const combinedNetworkAnchor = (items: NetworkItemLike[]): number => {
    const indexes = items.map((item, i) => (isNetworkGraphItem(item) ? i : -1)).filter(i => i >= 0);
    return indexes.length >= 2 ? indexes[indexes.length - 1] : -1;
};

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export const toPatternSummary = (item: NetworkItemLike, index: number): PatternSummary => {
    const entry = (key: string) => item.entries?.find(e => e.key === key)?.value;
    const subcategory = String(entry('informationSubcategory') || '');
    const title = subcategory.replace(/^Network Analysis:\s*/i, '') || item.value?.title || item.label || `Pattern ${index + 1}`;
    const context = itemMetadata(item).context || {};
    return {
        index,
        title,
        severity: String(entry('severity') || '').toLowerCase(),
        analysis: String(entry('analysis') || ''),
        entityIds: [...new Set(toList(context.entity_ids))],
        isSameParty: /same party/i.test(subcategory || title),
    };
};

/** Patterns ordered by severity, highest first; ties keep their original order. */
export const sortPatterns = (patterns: PatternSummary[]): PatternSummary[] =>
    [...patterns].sort((a, b) =>
        (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9) || a.index - b.index);
